import {
    Alert,
    Box,
    LinearProgress,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { ChevronDown, ChevronRight, RotateCcw, X } from 'lucide-react';
import { memo, useState } from 'react';
import { FormattedMessage, useIntl, type IntlShape } from 'react-intl';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import StatusLine from '~/components/Status/StatusLine';
import type { PatientRetrievals } from '../hooks';
import {
    cancellableRetrievalIds,
    orderedRetrievals,
    retrievalTone,
    type RetrievalSummary,
    type SeriesRetrieval,
} from '../model';
import { QuietButton, SYNC_TONE_COLORS } from '../shared';
import ModalityChip from '~/components/Chip/ModalityChip';

/**
 * The patient's series retrievals, each with where it stands and what can be done about it:
 * queued ones can be cancelled, failed and cancelled ones retried, and a transfer already under way
 * is shown running to its end. It stays after the import dialog has closed, which is the point of
 * it — the transfer outlives the dialog that asked for it.
 *
 * Nothing is drawn for a patient with no retrieval on record. Retrievals that cannot be read are
 * said to be unreadable, never drawn as none. The series list starts open while something is moving
 * or has failed, and folded once every series has settled without a failure.
 */
export default function PatientImportSection({
    retrievals: patientRetrievals,
}: {
    retrievals: PatientRetrievals;
}) {
    const intl = useIntl();
    const {
        retrievals,
        summary,
        cancel,
        cancelling,
        retry,
        retryingId,
        actionFailed,
        isError: readFailed,
    } = patientRetrievals;
    const needsAttention = summary.active || summary.failed > 0;
    const [expanded, setExpanded] = useState<boolean>();
    const open = expanded ?? needsAttention;

    const readFailedAlert = readFailed && (
        <Alert severity="error" sx={{ mx: 2, my: 1 }}>
            <FormattedMessage id="patientList.retrievals.readFailed" />
        </Alert>
    );

    if (summary.total === 0) {
        return readFailedAlert || null;
    }

    const cancellable = cancellableRetrievalIds(retrievals);

    return (
        <Box
            component="section"
            aria-label={intl.formatMessage({
                id: 'patientList.retrievals.region',
            })}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 3,
                    py: 1.25,
                    bgcolor: 'background.raised',
                }}
            >
                <QuietButton
                    aria-expanded={open}
                    onClick={() => setExpanded(!open)}
                    sx={{ gap: 0.75, minWidth: 0 }}
                >
                    {open ? (
                        <ChevronDown size={14} />
                    ) : (
                        <ChevronRight size={14} />
                    )}
                    <Typography
                        component="span"
                        sx={{ fontSize: 13, fontWeight: 600 }}
                    >
                        <FormattedMessage id="patientList.retrievals.title" />
                    </Typography>
                </QuietButton>
                <Typography
                    component="span"
                    noWrap
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 12,
                        color: 'text.secondary',
                    }}
                >
                    {summaryText(intl, summary)}
                </Typography>
                {cancellable.length > 0 && (
                    <QuietButton
                        disabled={cancelling}
                        onClick={() => cancel(cancellable)}
                    >
                        <FormattedMessage
                            id="patientList.retrievals.cancelQueued"
                            values={{ count: cancellable.length }}
                        />
                    </QuietButton>
                )}
            </Box>
            {readFailedAlert}
            {actionFailed && (
                <Alert severity="error" sx={{ mx: 2, my: 1 }}>
                    <FormattedMessage id="patientList.retrievals.actionFailed" />
                </Alert>
            )}
            {open &&
                orderedRetrievals(retrievals).map((retrieval) => (
                    <ImportSeriesRow
                        key={retrieval.retrievalId}
                        retrieval={retrieval}
                        onCancel={cancel}
                        onRetry={retry}
                        cancelling={cancelling}
                        retrying={retryingId === retrieval.retrievalId}
                    />
                ))}
        </Box>
    );
}

/** One requested series: its state in words, how far it has got, and the one action it offers. */
const ImportSeriesRow = memo(function ImportSeriesRow({
    retrieval,
    onCancel,
    onRetry,
    cancelling,
    retrying,
}: {
    retrieval: SeriesRetrieval;
    onCancel: (retrievalIds: readonly string[]) => void;
    onRetry: (retrievalId: string) => void;
    cancelling: boolean;
    retrying: boolean;
}) {
    const intl = useIntl();
    const theme = useTheme();
    const name =
        retrieval.seriesDescription ||
        intl.formatMessage({ id: 'patientList.import.pacs.untitledSeries' });
    const progress =
        retrieval.state === 'downloading' && retrieval.expected
            ? Math.min(100, (retrieval.received / retrieval.expected) * 100)
            : undefined;

    return (
        <Box
            data-testid="import-series-row"
            data-state={retrieval.state}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                px: 3,
                py: 1,
                borderTop: 1,
                borderColor: 'divider',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                {retrieval.modality && (
                    <ModalityChip modality={retrieval.modality} />
                )}
                <Typography
                    component="span"
                    noWrap
                    sx={{ flex: 1, minWidth: 0, fontSize: 13 }}
                >
                    <SensitiveInfoTypography>{name}</SensitiveInfoTypography>
                </Typography>
                <Tooltip
                    title={
                        retrieval.state === 'failed' && retrieval.failureReason
                            ? retrieval.failureReason
                            : ''
                    }
                >
                    <Box component="span" sx={{ flexShrink: 0 }}>
                        <StatusLine
                            label={stateText(intl, retrieval)}
                            color={SYNC_TONE_COLORS[
                                retrievalTone(retrieval.state)
                            ](theme)}
                            size={12}
                        />
                    </Box>
                </Tooltip>
                {retrieval.canRetry && (
                    <QuietButton
                        disabled={retrying}
                        onClick={() => onRetry(retrieval.retrievalId)}
                        aria-label={intl.formatMessage(
                            { id: 'patientList.retrievals.retrySeries' },
                            { name }
                        )}
                        sx={{ gap: 0.5 }}
                    >
                        <RotateCcw size={13} />
                        <FormattedMessage id="patientList.retrievals.retry" />
                    </QuietButton>
                )}
                {retrieval.canCancel && (
                    <QuietButton
                        disabled={cancelling}
                        onClick={() => onCancel([retrieval.retrievalId])}
                        aria-label={intl.formatMessage(
                            { id: 'patientList.retrievals.cancelSeries' },
                            { name }
                        )}
                    >
                        <X size={13} />
                    </QuietButton>
                )}
            </Box>
            {progress !== undefined && (
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    aria-hidden
                    sx={{ height: 3, borderRadius: 1 }}
                />
            )}
        </Box>
    );
});

function stateText(intl: IntlShape, retrieval: SeriesRetrieval): string {
    switch (retrieval.state) {
        case 'downloading':
            return retrieval.expected === undefined
                ? intl.formatMessage(
                      { id: 'patientList.retrievals.downloadingUnknown' },
                      { received: retrieval.received }
                  )
                : intl.formatMessage(
                      { id: 'patientList.retrievals.downloading' },
                      {
                          received: retrieval.received,
                          expected: retrieval.expected,
                      }
                  );
        case 'queued':
            return intl.formatMessage({ id: 'patientList.retrievals.queued' });
        case 'ready':
            return intl.formatMessage({ id: 'patientList.retrievals.ready' });
        case 'failed':
            return intl.formatMessage({ id: 'patientList.retrievals.failed' });
        case 'notExportable':
            return intl.formatMessage({
                id: 'patientList.retrievals.notExportable',
            });
        case 'emptyAtSource':
            return intl.formatMessage({
                id: 'patientList.retrievals.emptyAtSource',
            });
        case 'cancelled':
            return intl.formatMessage({
                id: 'patientList.retrievals.cancelled',
            });
    }
}

function summaryText(intl: IntlShape, summary: RetrievalSummary): string {
    const parts = [
        intl.formatMessage(
            { id: 'patientList.retrievals.summaryReady' },
            { ready: summary.ready, total: summary.total }
        ),
    ];
    const add = (count: number, id: string) => {
        if (count > 0) {
            parts.push(intl.formatMessage({ id }, { count }));
        }
    };
    add(summary.downloading, 'patientList.retrievals.summaryDownloading');
    add(summary.queued, 'patientList.retrievals.summaryQueued');
    add(summary.failed, 'patientList.retrievals.summaryFailed');
    add(summary.notExportable, 'patientList.retrievals.summaryNotExportable');
    add(summary.emptyAtSource, 'patientList.retrievals.summaryEmptyAtSource');
    add(summary.cancelled, 'patientList.retrievals.summaryCancelled');
    return parts.join(' · ');
}
